import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

const PURPLE = "#6f42c1";
const PURPLE_DARK = "#4e2d8c";
const PURPLE_LIGHT = "#f3f0fa";

const styles = {
  app: {
    minHeight: "100vh",
    background: "#f5f4f9",
    fontFamily: "'Segoe UI', sans-serif",
  },
  sidebar: {
    width: 240,
    minHeight: "100vh",
    background: "#fff",
    borderRight: "1px solid #e8e4f0",
    padding: "24px 0",
    position: "fixed",
    top: 0,
    left: 0,
    display: "flex",
    flexDirection: "column",
  },
  logo: {
    padding: "0 20px 24px",
    borderBottom: "1px solid #f0ecfa",
    marginBottom: 8,
  },
  logoText: {
    fontWeight: 700,
    fontSize: 18,
    color: PURPLE,
    letterSpacing: "-0.3px",
  },
  logoSub: {
    fontSize: 11,
    color: "#888",
    marginTop: 2,
  },
  navSection: {
    padding: "8px 12px 4px",
    fontSize: 10,
    fontWeight: 600,
    color: "#aaa",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 20px",
    cursor: "pointer",
    borderRadius: 8,
    margin: "2px 8px",
    fontSize: 13.5,
    color: "#555",
    transition: "all 0.15s",
  },
  navItemActive: {
    background: PURPLE_LIGHT,
    color: PURPLE,
    fontWeight: 600,
  },
  navIcon: {
    width: 20,
    height: 20,
    borderRadius: 5,
    background: "#e8e4f3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
  },
  navIconActive: {
    background: PURPLE,
    color: "#fff",
  },
  main: {
    marginLeft: 240,
    padding: "40px 40px 60px",
    minHeight: "100vh",
  },
  pageHeader: {
    marginBottom: 32,
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "#1a1a2e",
    margin: 0,
  },
  pageCrumb: {
    fontSize: 13,
    color: "#999",
    marginTop: 4,
  },
  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #ede9f7",
    padding: "28px 32px",
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#333",
    marginBottom: 20,
    paddingBottom: 14,
    borderBottom: "1px solid #f3f0fb",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20,
    marginBottom: 18,
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "#555",
    letterSpacing: "0.1px",
  },
  required: {
    color: PURPLE,
    marginLeft: 2,
  },
  input: {
    padding: "9px 13px",
    border: "1.5px solid #e5e1f3",
    borderRadius: 8,
    fontSize: 13.5,
    color: "#333",
    outline: "none",
    transition: "border-color 0.2s",
    background: "#faf9fe",
  },
  inputFocused: {
    borderColor: PURPLE,
    background: "#fff",
  },
  select: {
    padding: "9px 13px",
    border: "1.5px solid #e5e1f3",
    borderRadius: 8,
    fontSize: 13.5,
    color: "#555",
    outline: "none",
    background: "#faf9fe",
    cursor: "pointer",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236f42c1' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    paddingRight: 34,
  },
  error: {
    fontSize: 11.5,
    color: "#e05c5c",
    marginTop: 2,
  },
  submitBtn: {
    background: PURPLE,
    color: "#fff",
    border: "none",
    borderRadius: 9,
    padding: "10px 28px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.2s, transform 0.1s",
    marginTop: 4,
  },
  tableCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #ede9f7",
    overflow: "hidden",
    marginBottom: 24,
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 24px",
    borderBottom: "1px solid #f3f0fb",
  },
  tableTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#333",
  },
  addBtn: {
    background: PURPLE,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "7px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "10px 24px",
    fontSize: 11.5,
    fontWeight: 600,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    background: "#faf9fe",
    borderBottom: "1px solid #f0ecfa",
  },
  td: {
    padding: "13px 24px",
    fontSize: 13.5,
    color: "#444",
    borderBottom: "1px solid #f8f5ff",
  },
  editBtn: {
    background: PURPLE_LIGHT,
    color: PURPLE,
    border: "none",
    borderRadius: 6,
    padding: "5px 13px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  badge: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 11.5,
    fontWeight: 600,
    background: PURPLE_LIGHT,
    color: PURPLE,
  },
  hierarchyCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #ede9f7",
    padding: "28px 32px",
    marginBottom: 24,
  },
  hierarchyTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#333",
    marginBottom: 20,
  },
  hierRow: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    marginBottom: 4,
  },
  hierLevel: {
    display: "flex",
    alignItems: "center",
    padding: "10px 18px",
    borderRadius: 9,
    fontSize: 13,
    fontWeight: 500,
    color: "#fff",
    minWidth: 180,
    gap: 8,
  },
  hierLine: {
    width: 32,
    height: 2,
    background: "#d9d3f0",
  },
};

const hierLevels = [
  { label: "Region", bg: "#4e2d8c", icon: "🌐" },
  { label: "Regional Sales Manager", bg: "#6f42c1", icon: "👤" },
  { label: "Sales Manager", bg: "#7c55c9", icon: "👤" },
  { label: "Area", bg: "#8e6fd4", icon: "📍" },
  { label: "Area Sales Manager", bg: "#9f88dc", icon: "👤" },
  { label: "Senior Sales Executive", bg: "#b3a1e4", icon: "⭐" },
  { label: "Sales Person", bg: "#c8baed", icon: "💼" },
];

const commonSchema = {
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  userName: Yup.string().required("Username is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, "10-digit number")
    .required("Phone is required"),
  password: Yup.string()
    .min(6, "Min 6 characters")
    .required("Password is required"),
  address: Yup.string().required("Address is required"),
};

const PersonForm = ({ title, extraFields = [], onSubmit }) => {
  const [focused, setFocused] = useState(null);
  const schema = Yup.object({
    ...commonSchema,
    ...Object.fromEntries(
      extraFields.map((f) => [
        f.name,
        Yup.string().required(`${f.label} is required`),
      ]),
    ),
  });
  const initVals = {
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    phone: "",
    password: "",
    address: "",
    phoneNumber: "string",
    regionId: 0,
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>{title}</div>
      <Formik
        initialValues={initVals}
        validationSchema={schema}
        onSubmit={(vals, { resetForm }) => {
          onSubmit?.(vals);
          resetForm();
          alert("Submitted successfully!");
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <div style={styles.formRow}>
              {[
                {
                  name: "firstName",
                  label: "First name",
                  placeholder: "First Name",
                },
                {
                  name: "lastName",
                  label: "Last name",
                  placeholder: "Last Name",
                },
                {
                  name: "userName",
                  label: "User name",
                  placeholder: "Username",
                },
                {
                  name: "email",
                  label: "Email",
                  placeholder: "Email",
                  type: "email",
                },
                {
                  name: "phone",
                  label: "Phone No.",
                  placeholder: "Mobile number",
                },
                {
                  name: "password",
                  label: "Password",
                  placeholder: "Password",
                  type: "password",
                },
              ].map(({ name, label, placeholder, type = "text" }) => (
                <div key={name} style={styles.formGroup}>
                  <label style={styles.label}>
                    {label}
                    <span style={styles.required}>*</span>
                  </label>
                  <Field name={name}>
                    {({ field }) => (
                      <input
                        {...field}
                        type={type}
                        placeholder={placeholder}
                        style={{
                          ...styles.input,
                          ...(focused === name ? styles.inputFocused : {}),
                        }}
                        onFocus={() => setFocused(name)}
                        onBlur={(e) => {
                          setFocused(null);
                          field.onBlur(e);
                        }}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name={name}
                    render={(msg) => <div style={styles.error}>{msg}</div>}
                  />
                </div>
              ))}
            </div>

            <div
              style={{
                ...styles.formRow,
                gridTemplateColumns: extraFields.length ? "1fr 1fr" : "1fr 1fr",
              }}
            >
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Address<span style={styles.required}>*</span>
                </label>
                <Field name="address">
                  {({ field }) => (
                    <input
                      {...field}
                      placeholder="Address"
                      style={{
                        ...styles.input,
                        ...(focused === "address" ? styles.inputFocused : {}),
                      }}
                      onFocus={() => setFocused("address")}
                      onBlur={(e) => {
                        setFocused(null);
                        field.onBlur(e);
                      }}
                    />
                  )}
                </Field>
                <ErrorMessage
                  name="address"
                  render={(msg) => <div style={styles.error}>{msg}</div>}
                />
              </div>

              {extraFields.map(({ name, label, options }) => (
                <div key={name} style={styles.formGroup}>
                  <label style={styles.label}>
                    {label}
                    <span style={styles.required}>*</span>
                  </label>
                  <Field name={name} as="select" style={styles.select}>
                    <option value="">Select {label}</option>
                    {options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name={name}
                    render={(msg) => <div style={styles.error}>{msg}</div>}
                  />
                </div>
              ))}
            </div>

            <button
              type="submit"
              style={styles.submitBtn}
              disabled={isSubmitting}
            >
              Submit
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const RegionTable = ({ regions, onAdd }) => (
  <div style={styles.tableCard}>
    <div style={styles.tableHeader}>
      <span style={styles.tableTitle}>Regions</span>
      <button style={styles.addBtn} onClick={onAdd}>
        + Add Region
      </button>
    </div>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>#</th>
          <th style={styles.th}>Region Name</th>
          <th style={styles.th}>Status</th>
          <th style={styles.th}>Action</th>
        </tr>
      </thead>
      <tbody>
        {regions.map((r, i) => (
          <tr key={r}>
            <td style={styles.td}>{i + 1}</td>
            <td style={styles.td}>{r}</td>
            <td style={styles.td}>
              <span style={styles.badge}>Active</span>
            </td>
            <td style={styles.td}>
              <button style={styles.editBtn}>Edit</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AreaTable = ({ areas, onAdd }) => (
  <div style={styles.tableCard}>
    <div style={styles.tableHeader}>
      <span style={styles.tableTitle}>Areas</span>
      <button style={styles.addBtn} onClick={onAdd}>
        + Add Area
      </button>
    </div>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>#</th>
          <th style={styles.th}>Region</th>
          <th style={styles.th}>Area</th>
          <th style={styles.th}>Action</th>
        </tr>
      </thead>
      <tbody>
        {areas.map((a, i) => (
          <tr key={i}>
            <td style={styles.td}>{i + 1}</td>
            <td style={styles.td}>{a.region}</td>
            <td style={styles.td}>{a.area}</td>
            <td style={styles.td}>
              <button style={styles.editBtn}>Edit</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CreateRegionForm = ({ onSave }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>Create Region</div>
      <Formik
        initialValues={{ regionName: "" }}
        validationSchema={Yup.object({
          regionName: Yup.string().required("Region name is required"),
        })}
        onSubmit={(vals, { resetForm }) => {
          onSave(vals.regionName);
          resetForm();
          alert("Region created!");
        }}
      >
        {() => (
          <Form>
            <div style={{ maxWidth: 360, ...styles.formGroup }}>
              <label style={styles.label}>
                Region Name<span style={styles.required}>*</span>
              </label>
              <Field name="regionName">
                {({ field }) => (
                  <input
                    {...field}
                    placeholder="e.g. North, South..."
                    style={{
                      ...styles.input,
                      ...(focused ? styles.inputFocused : {}),
                    }}
                    onFocus={() => setFocused(true)}
                    onBlur={(e) => {
                      setFocused(false);
                      field.onBlur(e);
                    }}
                  />
                )}
              </Field>
              <ErrorMessage
                name="regionName"
                render={(msg) => <div style={styles.error}>{msg}</div>}
              />
            </div>
            <button
              type="submit"
              style={{ ...styles.submitBtn, marginTop: 16 }}
            >
              Add Region
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const CreateAreaForm = ({ regions, onSave }) => {
  const [focused, setFocused] = useState(null);
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>Create Area</div>
      <Formik
        initialValues={{ region: "", areaName: "" }}
        validationSchema={Yup.object({
          region: Yup.string().required("Region is required"),
          areaName: Yup.string().required("Area name is required"),
        })}
        onSubmit={(vals, { resetForm }) => {
          onSave(vals);
          resetForm();
          alert("Area created!");
        }}
      >
        {() => (
          <Form>
            <div style={{ ...styles.formRow, maxWidth: 720 }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Select Region<span style={styles.required}>*</span>
                </label>
                <Field name="region" as="select" style={styles.select}>
                  <option value="">Select Region</option>
                  {regions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Field>
                <ErrorMessage
                  name="region"
                  render={(msg) => <div style={styles.error}>{msg}</div>}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Area Name<span style={styles.required}>*</span>
                </label>
                <Field name="areaName">
                  {({ field }) => (
                    <input
                      {...field}
                      placeholder="e.g. Punjab, Kerala..."
                      style={{
                        ...styles.input,
                        ...(focused === "areaName" ? styles.inputFocused : {}),
                      }}
                      onFocus={() => setFocused("areaName")}
                      onBlur={(e) => {
                        setFocused(null);
                        field.onBlur(e);
                      }}
                    />
                  )}
                </Field>
                <ErrorMessage
                  name="areaName"
                  render={(msg) => <div style={styles.error}>{msg}</div>}
                />
              </div>
            </div>
            <button type="submit" style={styles.submitBtn}>
              Add Area
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const HierarchyView = () => (
  <div style={styles.hierarchyCard}>
    <div style={styles.hierarchyTitle}>Sales Organization Hierarchy</div>
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {hierLevels.map((h, i) => (
        <div key={h.label} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: i * 28, flexShrink: 0 }} />
          {i > 0 && (
            <div
              style={{
                width: 20,
                height: 2,
                background: "#d9d3f0",
                flexShrink: 0,
              }}
            />
          )}
          <div
            style={{
              ...styles.hierLevel,
              background: h.bg,
              marginBottom: i < hierLevels.length - 1 ? 6 : 0,
            }}
          >
            <span style={{ fontSize: 14 }}>{h.icon}</span>
            <span>{h.label}</span>
          </div>
        </div>
      ))}
    </div>
    <div
      style={{
        marginTop: 20,
        fontSize: 12.5,
        color: "#999",
        display: "flex",
        gap: 16,
      }}
    >
      <span>● Region → Regional Sales Manager → Sales Manager</span>
      <span>● Area → Area Sales Manager → Senior Executive → Sales Person</span>
    </div>
  </div>
);

const pages = [
  { key: "overview", label: "Overview", section: "Dashboard" },
  { key: "region", label: "Regions", section: "Setup" },
  { key: "area", label: "Areas", section: "Setup" },
  { key: "regional_mgr", label: "Regional Manager", section: "People" },
  { key: "sales_mgr", label: "Sales Manager", section: "People" },
  { key: "area_mgr", label: "Area Sales Manager", section: "People" },
  { key: "senior_exec", label: "Senior Executive", section: "People" },
  { key: "sales_person", label: "Sales Person", section: "People" },
];

export default function Sales() {
  const [active, setActive] = useState("overview");
  const [regions, setRegions] = useState(["North", "North-East", "South"]);
  const [areas, setAreas] = useState([
    { region: "North", area: "Punjab" },
    { region: "North", area: "Haryana" },
    { region: "North", area: "Rajasthan" },
    { region: "South", area: "Kerala" },
    { region: "South", area: "Karnataka" },
  ]);

  const grouped = pages.reduce((acc, p) => {
    if (!acc[p.section]) acc[p.section] = [];
    acc[p.section].push(p);
    return acc;
  }, {});

  const renderContent = () => {
    switch (active) {
      case "overview":
        return (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 16,
                marginBottom: 28,
              }}
            >
              {[
                { label: "Total Regions", value: regions.length },
                { label: "Total Areas", value: areas.length },
                { label: "Hierarchy Levels", value: 6 },
              ].map((m) => (
                <div
                  key={m.label}
                  style={{
                    background: "#fff",
                    border: "1px solid #ede9f7",
                    borderRadius: 12,
                    padding: "20px 24px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#999",
                      marginBottom: 8,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {m.label}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: PURPLE }}>
                    {m.value}
                  </div>
                </div>
              ))}
            </div>
            <HierarchyView />
          </>
        );
      case "region":
        return (
          <>
            <CreateRegionForm
              onSave={(name) => setRegions((r) => [...r, name])}
            />
            <RegionTable regions={regions} onAdd={() => {}} />
          </>
        );
      case "area":
        return (
          <>
            <CreateAreaForm
              regions={regions}
              onSave={(v) =>
                setAreas((a) => [...a, { region: v.region, area: v.areaName }])
              }
            />
            <AreaTable areas={areas} onAdd={() => {}} />
          </>
        );
      case "regional_mgr":
        return (
          <PersonForm
            title="Create Regional Sales Manager"
            extraFields={[
              { name: "region", label: "Region", options: regions },
            ]}
          />
        );
      case "sales_mgr":
        return (
          <PersonForm
            title="Create Sales Manager"
            extraFields={[
              {
                name: "regionalManager",
                label: "Regional Sales Manager",
                options: ["RSM - North", "RSM - South", "RSM - East"],
              },
            ]}
          />
        );
      case "area_mgr":
        return (
          <PersonForm
            title="Create Area Sales Manager"
            extraFields={[
              {
                name: "area",
                label: "Area",
                options: areas.map((a) => a.area),
              },
              {
                name: "salesManager",
                label: "Sales Manager",
                options: ["SM - North", "SM - South", "SM - East"],
              },
            ]}
          />
        );
      case "senior_exec":
        return (
          <PersonForm
            title="Create Senior Sales Executive"
            extraFields={[
              {
                name: "areaSalesManager",
                label: "Area Sales Manager",
                options: ["ASM - Punjab", "ASM - Haryana", "ASM - Kerala"],
              },
            ]}
          />
        );
      case "sales_person":
        return (
          <PersonForm
            title="Create Sales Person"
            extraFields={[
              {
                name: "seniorExec",
                label: "Senior Sales Executive",
                options: ["SSE - North", "SSE - South", "SSE - East"],
              },
            ]}
          />
        );
      default:
        return null;
    }
  };

  const currentPage = pages.find((p) => p.key === active);

  return (
    <div style={styles.app}>
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <div style={styles.logoText}>⬡ SalesForce</div>
          <div style={styles.logoSub}>Sales Management System</div>
        </div>
        {Object.entries(grouped).map(([section, items]) => (
          <div key={section}>
            <div style={styles.navSection}>{section}</div>
            {items.map((p) => (
              <div
                key={p.key}
                style={{
                  ...styles.navItem,
                  ...(active === p.key ? styles.navItemActive : {}),
                }}
                onClick={() => setActive(p.key)}
              >
                <div
                  style={{
                    ...styles.navIcon,
                    ...(active === p.key ? styles.navIconActive : {}),
                  }}
                >
                  {p.key === "overview"
                    ? "◈"
                    : p.key.includes("region")
                      ? "R"
                      : p.key === "area"
                        ? "A"
                        : p.key.includes("mgr")
                          ? "M"
                          : p.key.includes("exec")
                            ? "E"
                            : "S"}
                </div>
                {p.label}
              </div>
            ))}
          </div>
        ))}
      </div>

      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>{currentPage?.label}</h1>
          <div style={styles.pageCrumb}>
            Sales Module › {currentPage?.section} › {currentPage?.label}
          </div>
        </div>
        {renderContent()}
      </main>
    </div>
  );
}
